import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import technicalMaintenanceApi, { EQUIPMENT_CATEGORIES } from '../../api/technicalMaintenanceApi';
import './technicalMaintenanceShared.css';
import TopMaintenanceEquipmentPanel from './TopMaintenanceEquipmentPanel';
import MaintenanceAlertsPanel from './MaintenanceAlertsPanel';

const MONTH_OPTIONS = [
  { value: '', label: 'Tüm yıl' },
  { value: '1', label: 'Ocak' },
  { value: '2', label: 'Şubat' },
  { value: '3', label: 'Mart' },
  { value: '4', label: 'Nisan' },
  { value: '5', label: 'Mayıs' },
  { value: '6', label: 'Haziran' },
  { value: '7', label: 'Temmuz' },
  { value: '8', label: 'Ağustos' },
  { value: '9', label: 'Eylül' },
  { value: '10', label: 'Ekim' },
  { value: '11', label: 'Kasım' },
  { value: '12', label: 'Aralık' },
];

const MONTH_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const CHART_COLORS = ['#2563EB', '#059669', '#D97706', '#7C3AED', '#DC2626', '#0891B2'];
const CATEGORY_COLORS = {
  machine_list: '#2563EB',
  cold_room: '#059669',
  cabinet: '#7C3AED',
  ice_machine: '#D97706',
};

const fmt = (n, digits = 0) =>
  (Number(n) || 0).toLocaleString('tr-TR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const parseStatsResponse = (response) => {
  if (response?.total_equipment !== undefined || response?.success) {
    return response;
  }
  return response?.data || null;
};

function ChartTooltip({ active, payload, label, unit = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="tm-chart-tooltip">
      <div className="tm-chart-tooltip-title">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey || entry.name} className="tm-chart-tooltip-row">
          <span className="dot" style={{ background: entry.color || entry.payload?.fill }} />
          <span>{entry.name}</span>
          <span className="val">
            {fmt(entry.value)}
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
}

const TechnicalMaintenanceDashboardPage = () => {
  const currentYear = new Date().getFullYear();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceYear, setInvoiceYear] = useState(String(currentYear));
  const [invoiceMonth, setInvoiceMonth] = useState('');
  const isInitialLoad = useRef(true);

  const yearOptions = useMemo(
    () => Array.from({ length: 6 }, (_, index) => String(currentYear - index)),
    [currentYear]
  );

  useEffect(() => {
    const load = async () => {
      if (isInitialLoad.current) {
        setLoading(true);
      } else {
        setInvoiceLoading(true);
      }
      setError(null);
      try {
        const params = { invoice_year: invoiceYear };
        if (invoiceMonth) {
          params.invoice_month = invoiceMonth;
        }
        const response = await technicalMaintenanceApi.getDashboardStats(params);
        setStats(parseStatsResponse(response));
      } catch (err) {
        setError(err.message || 'Özet bilgiler yüklenemedi.');
      } finally {
        setLoading(false);
        setInvoiceLoading(false);
        isInitialLoad.current = false;
      }
    };
    load();
  }, [invoiceYear, invoiceMonth]);

  const categoryStats = useMemo(() => {
    return EQUIPMENT_CATEGORIES.map((cat) => {
      const found = (stats?.by_category || []).find((item) => item.category === cat.value);
      return {
        category: cat.value,
        label: cat.label,
        shortLabel: cat.label.replace(' Listesi', '').replace('Buz Makinesi', 'Buz M.'),
        count: found?.count ?? 0,
        fill: CATEGORY_COLORS[cat.value] || CHART_COLORS[0],
      };
    });
  }, [stats]);

  const totalCategoryCount = useMemo(
    () => categoryStats.reduce((sum, item) => sum + item.count, 0),
    [categoryStats]
  );

  const equipmentStatusChart = useMemo(
    () => [
      { name: 'Aktif', value: stats?.active_equipment ?? 0, fill: '#059669' },
      { name: 'Pasif', value: stats?.inactive_equipment ?? 0, fill: '#94A3B8' },
    ],
    [stats]
  );

  const activeRate = stats?.total_equipment
    ? Math.round((stats.active_equipment / stats.total_equipment) * 100)
    : 0;

  const avgInvoice = stats?.invoice_count
    ? Number(stats.invoice_total || 0) / stats.invoice_count
    : 0;

  const invoicePeriodLabel = invoiceMonth
    ? MONTH_OPTIONS.find((item) => item.value === invoiceMonth)?.label
    : 'Tüm yıl';

  const toggleMonth = (monthValue) => {
    setInvoiceMonth((prev) => (prev === monthValue ? '' : monthValue));
  };

  if (loading) {
    return (
      <div className="tm-page flex items-center justify-center min-h-[320px]">
        <div className="text-blue-600 dark:text-blue-400 animate-pulse font-medium">Pano yükleniyor...</div>
      </div>
    );
  }

  if (error && !stats) {
    return <div className="tm-page text-red-600">{error}</div>;
  }

  const kpis = [
    { key: 'total', label: 'Toplam Ekipman', value: stats?.total_equipment ?? 0, unit: 'adet', accent: '#2563EB' },
    { key: 'active', label: 'Aktif Ekipman', value: stats?.active_equipment ?? 0, unit: 'adet', accent: '#059669' },
    { key: 'inactive', label: 'Pasif Ekipman', value: stats?.inactive_equipment ?? 0, unit: 'adet', accent: '#64748B' },
    { key: 'open', label: 'Açık Bakım', value: stats?.open_maintenance ?? 0, unit: 'kayıt', accent: '#D97706' },
    { key: 'pending', label: 'Bekleyen Fatura', value: stats?.pending_invoices ?? 0, unit: 'adet', accent: '#DC2626' },
    { key: 'rate', label: 'Aktif Oran', value: activeRate, unit: '%', accent: '#0891B2', digits: 0 },
  ];

  return (
    <div className="tm-page space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Teknik Bakım Panosu</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Ekipman envanteri, bakım durumu ve fatura özetleri — tek bakışta
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="tm-kpi-grid">
        {kpis.map((kpi) => (
          <div key={kpi.key} className="tm-kpi-card" style={{ '--tm-accent': kpi.accent }}>
            <span className="tm-kpi-label">{kpi.label}</span>
            <span className="tm-kpi-value">
              {fmt(kpi.value, kpi.digits ?? 0)}
              <small>{kpi.unit}</small>
            </span>
          </div>
        ))}
      </div>

      <MaintenanceAlertsPanel />

      <div className="tm-dash-card">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-5">
          <div>
            <h3 className="!mb-1">Fatura Dönemi</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {invoiceYear} · {invoicePeriodLabel}
            </p>
          </div>
          <div className="tm-dash-toolbar">
            <div className="tm-filter-group">
              <span className="tm-filter-group-label">Yıl</span>
              <select
                className="tm-filter-select"
                value={invoiceYear}
                onChange={(e) => setInvoiceYear(e.target.value)}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="tm-month-chips">
              {MONTH_SHORT.map((label, index) => {
                const monthValue = String(index + 1);
                const active = invoiceMonth === monthValue;
                return (
                  <button
                    key={label}
                    type="button"
                    className={`tm-month-chip${active ? ' active' : ''}`}
                    onClick={() => toggleMonth(monthValue)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="tm-month-chip"
              onClick={() => setInvoiceMonth('')}
            >
              Tüm yıl
            </button>
          </div>
        </div>

        <div className={`tm-invoice-highlight ${invoiceLoading ? 'opacity-60' : ''}`}>
          <div>
            <div className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Toplam Fatura Tutarı</div>
            <div className="tm-invoice-amount">
              {invoiceLoading ? '...' : `${fmt(stats?.invoice_total || 0, 2)} TRY`}
            </div>
          </div>
          <div className="text-right space-y-1">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-white">{stats?.invoice_count ?? 0}</span> fatura
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Ortalama: {fmt(avgInvoice, 2)} TRY
            </div>
          </div>
        </div>
      </div>

      <div className="tm-dash-grid">
        <div className="tm-dash-card">
          <h3>Kategori Dağılımı</h3>
          <div className="tm-dash-chart">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={categoryStats} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis
                  dataKey="shortLabel"
                  tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={{ stroke: '#CBD5E1' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip unit=" adet" />} cursor={{ fill: 'rgba(37, 99, 235, 0.08)' }} />
                <Bar dataKey="count" name="Ekipman" radius={[6, 6, 0, 0]} maxBarSize={48}>
                  {categoryStats.map((entry) => (
                    <Cell key={entry.category} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="tm-dash-card">
          <h3>Ekipman Durumu</h3>
          <div className="tm-dash-chart">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={equipmentStatusChart}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={3}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {equipmentStatusChart.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip unit=" adet" />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="tm-dash-card">
          <h3>Kategori Payları</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4 font-medium">Kategori</th>
                  <th className="py-2 pr-4 font-medium text-right">Adet</th>
                  <th className="py-2 font-medium">Pay</th>
                </tr>
              </thead>
              <tbody>
                {categoryStats.map((item) => {
                  const share = totalCategoryCount ? (item.count / totalCategoryCount) * 100 : 0;
                  return (
                    <tr key={item.category} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-3 pr-4 font-medium text-gray-900 dark:text-white">{item.label}</td>
                      <td className="py-3 pr-4 text-right text-gray-700 dark:text-gray-300">{fmt(item.count)}</td>
                      <td className="py-3">
                        <div className="tm-share-cell">
                          <span className="tm-share-bar">
                            <span style={{ width: `${Math.min(100, share)}%`, background: item.fill }} />
                          </span>
                          <span className="tm-share-num">%{fmt(share, 1)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold text-gray-900 dark:text-white">
                  <td className="pt-3 pr-4">Toplam</td>
                  <td className="pt-3 pr-4 text-right">{fmt(totalCategoryCount)}</td>
                  <td className="pt-3 tm-share-num">%100</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <TopMaintenanceEquipmentPanel currentYear={currentYear} yearOptions={yearOptions} />
    </div>
  );
};

export default TechnicalMaintenanceDashboardPage;
